import {Button, Hotkey} from '@wandb/components';
import React, {useCallback, useEffect, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {getPanelSpec, type PanelType} from '../../../util/panelTypes';
import {PanelRunButton} from '../../PanelMarimo/PanelRunButton';
import {ToggleMinimizeRunSelectorButton} from '../../ToggleMinimizeRunSelectorButton';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useGoToBaseUrl} from '../hooks/hooks';
import {usePanelContext} from '../PanelContextProvider';
import {FullScreenPanelEditButton} from '../PanelEditButton';
import {FullScreenPanelRunMetricButton} from '../PanelRunMetricButton';
import {useNextAndPreviousPanel} from '../useNextAndPreviousPanel';
import {FullScreenPanelOverflowMenu} from './FullScreenPanelOverflowMenu';

const PREVIOUS_PANEL_HOTKEYS = ['ArrowLeft'] as const;
const NEXT_PANEL_HOTKEYS = ['ArrowRight'] as const;

export const FullScreenPanelHeader = ({
  className,
  panelBankSectionConfigRef,
  panelType,
  isMobileWorkspaceTab,
  isRunSelectorMinimized,
  toggleMinimizeRunSelectorWithTracking,
}: {
  className?: string;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelType: PanelType;
  isMobileWorkspaceTab?: boolean;
  isRunSelectorMinimized?: boolean;
  toggleMinimizeRunSelectorWithTracking?: (method: 'hotkey' | 'button') => void;
}) => {
  const {panelRef, panelPopupMode} = usePanelContext();
  const goToBaseURL = useGoToBaseUrl();
  const workspaceDrawerContext = useWorkspaceDrawerContext();

  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  const {goToPreviousPanel, goToNextPanel, hasPreviousPanel, hasNextPanel} =
    useNextAndPreviousPanel({panelBankSectionConfigRef, panelRef});

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      // A layer that consumed this Escape (e.g. a dismissing menu) prevents
      // default; exiting fullscreen too would double-act one keypress.
      if (event.key === 'Escape' && !event.defaultPrevented) {
        // Don't navigate back if a drawer is open - let the drawer handle ESC
        if (workspaceDrawerContext?.contentType) {
          return;
        }
        goToBaseURL();
      }
    };
    // TODO: migrate full-screen panel Escape handling to the inputSystem
    // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [goToBaseURL, panelPopupMode, workspaceDrawerContext?.contentType]);

  const onToggleMinimizeRunSelector = useCallback(() => {
    toggleMinimizeRunSelectorWithTracking?.('button');
  }, [toggleMinimizeRunSelectorWithTracking]);

  return (
    <div
      className={twMerge('flex flex-col', 'min-h-[32px]', className)}
      data-test="panel-header">
      <div className="flex w-full justify-between">
        <div className="flex gap-8">
          {isRunSelectorMinimized &&
            !isMobileWorkspaceTab &&
            toggleMinimizeRunSelectorWithTracking && (
              <ToggleMinimizeRunSelectorButton
                variant="secondary"
                toMinimize={false}
                onToggle={onToggleMinimizeRunSelector}
              />
            )}
          <Button
            aria-label="Back"
            startIcon="back"
            variant="secondary"
            onPress={e => {
              e.continuePropagation();
              goToBaseURL();
            }}>
            Workspace
          </Button>
        </div>

        <div className="flex gap-8">
          <Button
            aria-label="Previous panel"
            startIcon="chevron-back"
            onPress={e => {
              goToPreviousPanel();
              (e.target as HTMLElement).blur();
            }}
            isDisabled={!hasPreviousPanel}
            variant="secondary"
            tooltip={{
              children: (
                <>
                  <span style={{margin: '2px'}}>Previous panel</span>
                  <Hotkey
                    keys={PREVIOUS_PANEL_HOTKEYS}
                    name="Previous panel"
                    m={2}
                  />
                </>
              ),
            }}>
            Previous
          </Button>
          <Button
            aria-label="Next panel"
            endIcon="chevron-next"
            onPress={e => {
              goToNextPanel();
              (e.target as HTMLElement).blur();
            }}
            isDisabled={!hasNextPanel}
            variant="secondary"
            tooltip={{
              children: (
                <>
                  <span style={{margin: '2px'}}>Next panel</span>
                  <Hotkey keys={NEXT_PANEL_HOTKEYS} name="Next panel" m={2} />
                </>
              ),
            }}>
            Next
          </Button>
        </div>
      </div>

      {/*
        NOTE: If this row is removed or its height changed, update the height
        calculations in 'PanelMediaBrowser'; see
        frontends/app/src/components/PanelMediaBrowser/PanelBody.tsx.
      */}
      <div className="mt-24 flex w-full justify-end">
        <div className="flex gap-8">
          <FullScreenPanelRunMetricButton
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelRef={panelRef}
            panelType={panelType}
          />
          <FullScreenPanelEditButton />
          {panelType === 'Marimo' && (
            <PanelRunButton panelRef={panelRef} buttonSize="md" />
          )}
          <FullScreenPanelOverflowMenu
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelSpec={panelSpec}
          />
        </div>
      </div>
    </div>
  );
};
